import { Check, Copy } from 'lucide-react';
import {
    type KeyboardEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    type DynamicWidgetBindingTarget,
    highlightRenderedDynamicWidgetBinding,
    readDynamicWidgetBindingTarget,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_binding_dom';
import {
    buildDynamicWidgetBindingSkeleton,
    type DynamicWidgetSkeletonNode,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_binding_skeleton';
import {
    DYNAMIC_WIDGET_INSPECTOR_RAW_VALUE_MAX_CHARS,
    formatDynamicWidgetRawValue,
    serializeDynamicWidgetDebugSnapshot,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_debug_snapshot';
import {
    getDynamicWidgetErrorCatalogEntry,
    getDynamicWidgetIssueBody,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_error_catalog';
import {
    dynamicWidgetInspectorStore,
    useDynamicWidgetInspectorState,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_inspector_store';
import {
    type DynamicWidgetBinding,
    type DynamicWidgetIssue,
    type DynamicWidgetModel,
    type DynamicWidgetSource,
    getDynamicWidgetSourceId,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { Button } from '@apps/web/components/ui/button';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@apps/web/components/ui/tabs';
import { cn } from '@apps/web/lib/utils';

/*
 * Binding inspector for the dynamic agent UI, rendered as a tab of the chat
 * debug sidebar (`phoebe_chat_debug_sidebar.tsx`). That sidebar mounts only
 * for platform admins with the debug panel open, so this surface inherits the
 * internal-admin gate; it deliberately adds no route of its own.
 *
 * Selection is symmetric: clicking a bound element in the rendered card
 * selects its skeleton node, and selecting a skeleton node highlights and
 * scrolls to the rendered element.
 */

type InspectorView = 'generated-ui' | 'skeleton' | 'source' | 'issues';

const INSPECTOR_LISTBOX_ID = 'dynamic-widget-binding-skeleton';

function DetailRow({
    label,
    value,
    isMonospace = true,
}: {
    readonly label: string;
    readonly value: string;
    readonly isMonospace?: boolean;
}) {
    return (
        <div className="border-border/70 min-w-0 border-b py-2">
            <div className="text-muted-foreground text-[11px] font-medium">
                {label}
            </div>
            <div
                className={cn(
                    'text-xs break-words whitespace-pre-wrap',
                    isMonospace && 'font-mono',
                )}
            >
                {value}
            </div>
        </div>
    );
}

function describeSource(source: DynamicWidgetSource): Array<{
    readonly label: string;
    readonly value: string;
}> {
    if (source.kind === 'event_source') {
        return [
            { label: 'Source kind', value: 'event_source' },
            { label: 'Tool', value: source.toolName },
            { label: 'Call ID', value: source.callId },
            { label: 'Tool call event', value: source.toolCallEventId },
            {
                label: 'Tool output event',
                value: source.toolOutputEventId ?? 'none',
            },
            { label: 'Phase', value: source.phase },
            { label: 'Captured at', value: source.capturedAt },
            { label: 'Organization', value: source.organizationId },
            { label: 'Mode', value: source.mode },
        ];
    }
    return [
        { label: 'Source kind', value: 'resource_source' },
        { label: 'Resource type', value: source.resourceType },
        { label: 'Resource ID', value: source.resourceId },
        { label: 'API path', value: source.apiPath },
        { label: 'Fetched at', value: source.fetchedAt ?? 'not fetched yet' },
        { label: 'Organization', value: source.organizationId },
        { label: 'Mode', value: source.mode },
    ];
}

function SkeletonNodeRow({
    node,
    isSelected,
    isActive,
    onSelect,
}: {
    readonly node: DynamicWidgetSkeletonNode;
    readonly isSelected: boolean;
    readonly isActive: boolean;
    readonly onSelect: () => void;
}) {
    return (
        <div
            id={`${INSPECTOR_LISTBOX_ID}-${node.key}`}
            role="option"
            aria-selected={isSelected}
            data-skeleton-node-key={node.key}
            data-skeleton-node-kind={node.kind}
            onClick={onSelect}
            className={cn(
                'flex cursor-pointer items-center gap-1.5 rounded-sm px-1.5 py-1 font-mono text-[11px]',
                isActive && 'ring-ring/50 ring-[3px]',
                isSelected
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-transparent',
            )}
            style={{ paddingLeft: `${node.depth * 12 + 6}px` }}
        >
            <span className="text-muted-foreground">{node.kind}</span>
            <span className="min-w-0 truncate">{node.label}</span>
            {node.issueCodes.length > 0 && node.kind !== 'issue' ? (
                <span className="text-destructive shrink-0">
                    ({node.issueCodes.length})
                </span>
            ) : null}
        </div>
    );
}

function BindingSkeletonView({
    model,
    selectedBindingId,
    onSelectBinding,
}: {
    readonly model: DynamicWidgetModel;
    readonly selectedBindingId: string | null;
    readonly onSelectBinding: (bindingId: string | null) => void;
}) {
    const nodes = useMemo(
        () => buildDynamicWidgetBindingSkeleton(model),
        [model],
    );
    const [activeIndex, setActiveIndex] = useState(0);

    // Follow an external selection (a click on the rendered card) so the
    // keyboard cursor and the selection never drift apart.
    useEffect(() => {
        if (selectedBindingId === null) {
            return;
        }
        const index = nodes.findIndex(
            (node) => node.bindingId === selectedBindingId,
        );
        if (index >= 0) {
            setActiveIndex(index);
        }
    }, [nodes, selectedBindingId]);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (nodes.length === 0) {
            return;
        }
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setActiveIndex(Math.min(activeIndex + 1, nodes.length - 1));
                return;
            case 'ArrowUp':
                event.preventDefault();
                setActiveIndex(Math.max(activeIndex - 1, 0));
                return;
            case 'Home':
                event.preventDefault();
                setActiveIndex(0);
                return;
            case 'End':
                event.preventDefault();
                setActiveIndex(nodes.length - 1);
                return;
            case 'Enter':
            case ' ':
                event.preventDefault();
                onSelectBinding(nodes[activeIndex]?.bindingId ?? null);
                return;
            default:
                return;
        }
    };

    if (nodes.length === 0) {
        return (
            <p className="text-muted-foreground px-3 py-3 text-xs">
                This widget has no bindings.
            </p>
        );
    }

    const activeNode = nodes[activeIndex];
    return (
        <div
            role="listbox"
            tabIndex={0}
            aria-label="Binding skeleton"
            aria-activedescendant={
                activeNode === undefined
                    ? undefined
                    : `${INSPECTOR_LISTBOX_ID}-${activeNode.key}`
            }
            onKeyDown={handleKeyDown}
            className="focus-visible:ring-ring/50 space-y-0.5 px-1.5 py-2 outline-none focus-visible:ring-[3px]"
        >
            {nodes.map((node, index) => (
                <SkeletonNodeRow
                    key={node.key}
                    node={node}
                    isActive={index === activeIndex}
                    isSelected={
                        node.bindingId !== null &&
                        node.bindingId === selectedBindingId
                    }
                    onSelect={() => {
                        setActiveIndex(index);
                        onSelectBinding(node.bindingId);
                    }}
                />
            ))}
        </div>
    );
}

function SourceView({
    model,
    binding,
}: {
    readonly model: DynamicWidgetModel;
    readonly binding: DynamicWidgetBinding | null;
}) {
    if (binding === null) {
        return (
            <p className="text-muted-foreground px-3 py-3 text-xs">
                Select a binding in the card or the skeleton to see where its
                value came from.
            </p>
        );
    }
    const sources = model.sources.filter((source) =>
        binding.sourceIds.includes(getDynamicWidgetSourceId(source)),
    );
    return (
        <div className="px-3 pb-4">
            <DetailRow label="Binding" value={binding.id} />
            <DetailRow label="Kind" value={binding.kind} />
            <DetailRow
                label="Field paths"
                value={
                    binding.paths.length === 0
                        ? 'none'
                        : binding.paths.join('\n')
                }
            />
            <DetailRow label="Transform" value={binding.transform ?? 'none'} />
            <DetailRow
                label="Raw value"
                value={formatDynamicWidgetRawValue(
                    binding.rawValue,
                    DYNAMIC_WIDGET_INSPECTOR_RAW_VALUE_MAX_CHARS,
                )}
            />
            <DetailRow
                label="Rendered value"
                value={binding.renderedValue ?? 'none'}
            />
            <DetailRow label="Freshness" value={binding.freshness} />
            {sources.length === 0 ? (
                <DetailRow label="Source" value="none" />
            ) : (
                sources.flatMap((source) =>
                    describeSource(source).map((detail) => (
                        <DetailRow
                            key={`${getDynamicWidgetSourceId(source)}-${detail.label}`}
                            label={detail.label}
                            value={detail.value}
                        />
                    )),
                )
            )}
        </div>
    );
}

function IssuesView({
    issues,
    onSelectBinding,
}: {
    readonly issues: ReadonlyArray<DynamicWidgetIssue>;
    readonly onSelectBinding: (bindingId: string | null) => void;
}) {
    if (issues.length === 0) {
        return (
            <p className="text-muted-foreground px-3 py-3 text-xs">
                No issues on this widget.
            </p>
        );
    }
    return (
        <div className="px-3 pb-4">
            {issues.map((issue) => {
                const catalog = getDynamicWidgetErrorCatalogEntry(issue.code);
                return (
                    <div
                        key={issue.id}
                        className="border-border/70 border-b py-2"
                    >
                        <div className="flex items-center gap-1.5">
                            <span className="font-mono text-[11px]">
                                {issue.code}
                            </span>
                            <span className="text-muted-foreground font-mono text-[10px]">
                                {issue.level} · {issue.scope}
                            </span>
                        </div>
                        <p className="text-xs font-medium">{catalog.title}</p>
                        <p className="text-muted-foreground text-xs">
                            {getDynamicWidgetIssueBody(issue)}
                        </p>
                        {issue.bindingIds.length > 0 ? (
                            <div className="mt-1 flex flex-wrap gap-1">
                                {issue.bindingIds.map((bindingId) => (
                                    <Button
                                        key={bindingId}
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="h-6 px-2 font-mono text-[10px]"
                                        onClick={() =>
                                            onSelectBinding(bindingId)
                                        }
                                    >
                                        {bindingId}
                                    </Button>
                                ))}
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

function CopySnapshotButton({ model }: { readonly model: DynamicWidgetModel }) {
    const [hasCopied, setHasCopied] = useState(false);

    useEffect(() => {
        setHasCopied(false);
    }, [model]);

    return (
        <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => {
                const snapshot = serializeDynamicWidgetDebugSnapshot(model);
                void navigator.clipboard?.writeText(snapshot);
                setHasCopied(true);
            }}
        >
            {hasCopied ? (
                <Check className="size-3.5" />
            ) : (
                <Copy className="size-3.5" />
            )}
            {hasCopied ? 'Snapshot copied' : 'Copy snapshot'}
        </Button>
    );
}

export function DynamicWidgetDataInspector() {
    const { models, selection } = useDynamicWidgetInspectorState();
    const [view, setView] = useState<InspectorView>('skeleton');

    const selectedModel =
        models.find((model) => model.widgetId === selection?.widgetId) ??
        models[models.length - 1] ??
        null;
    const selectedBindingId =
        selectedModel !== null && selection?.widgetId === selectedModel.widgetId
            ? selection.bindingId
            : null;
    const selectedBinding =
        selectedModel === null || selectedBindingId === null
            ? null
            : (selectedModel.bindings.find(
                  (binding) => binding.id === selectedBindingId,
              ) ?? null);

    // Selecting inside the skeleton must not yank the user off the skeleton
    // tab; a selection from anywhere else is a request to see the source.
    const isSkeletonSelectionRef = useRef(false);
    const selectBinding = useCallback(
        (
            widgetId: string,
            bindingId: string | null,
            origin: 'skeleton' | 'other' = 'other',
        ) => {
            isSkeletonSelectionRef.current = origin === 'skeleton';
            dynamicWidgetInspectorStore.select(widgetId, bindingId);
        },
        [],
    );

    // Rendered card -> skeleton. A capture listener keeps the widgets free of
    // inspector-specific click handlers.
    useEffect(() => {
        function handleDocumentClick(event: MouseEvent) {
            const target: DynamicWidgetBindingTarget | null =
                readDynamicWidgetBindingTarget(event.target);
            if (target === null) {
                return;
            }
            isSkeletonSelectionRef.current = false;
            dynamicWidgetInspectorStore.select(
                target.widgetId,
                target.bindingId,
            );
        }
        document.addEventListener('click', handleDocumentClick, true);
        return () => {
            document.removeEventListener('click', handleDocumentClick, true);
        };
    }, []);

    // Skeleton -> rendered card.
    useEffect(() => {
        if (selectedModel === null || selectedBindingId === null) {
            highlightRenderedDynamicWidgetBinding(null);
            return;
        }
        highlightRenderedDynamicWidgetBinding({
            widgetId: selectedModel.widgetId,
            bindingId: selectedBindingId,
        });
    }, [selectedBindingId, selectedModel]);

    // A binding picked outside the skeleton (rendered card, issue list, "Show
    // source") is a request to see that binding's source.
    useEffect(() => {
        if (selectedBindingId === null) {
            return;
        }
        if (isSkeletonSelectionRef.current) {
            isSkeletonSelectionRef.current = false;
            return;
        }
        setView('source');
    }, [selectedBindingId]);

    if (selectedModel === null) {
        return (
            <div className="px-3 py-3">
                <p className="text-muted-foreground text-xs">
                    No dynamic widgets in this conversation yet. Widget models
                    register while this panel is open.
                </p>
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="flex flex-wrap items-center gap-1.5 px-3 pt-2 pb-1">
                {models.length > 1
                    ? models.map((model) => (
                          <Button
                              key={model.widgetId}
                              type="button"
                              size="sm"
                              variant={
                                  model.widgetId === selectedModel.widgetId
                                      ? 'secondary'
                                      : 'ghost'
                              }
                              className="h-6 px-2 font-mono text-[10px]"
                              onClick={() =>
                                  selectBinding(model.widgetId, null)
                              }
                          >
                              {model.template} · {model.widgetId.slice(0, 8)}
                          </Button>
                      ))
                    : null}
                <CopySnapshotButton model={selectedModel} />
            </div>
            <Tabs
                value={view}
                onValueChange={(nextView) => setView(nextView as InspectorView)}
                className="min-h-0 flex-1 gap-0"
            >
                <div className="border-b px-3 py-2">
                    <TabsList className="grid h-8 w-full grid-cols-4 rounded-md">
                        <TabsTrigger
                            className="text-[11px]"
                            value="generated-ui"
                        >
                            Generated UI
                        </TabsTrigger>
                        <TabsTrigger className="text-[11px]" value="skeleton">
                            Skeleton
                        </TabsTrigger>
                        <TabsTrigger className="text-[11px]" value="source">
                            Source
                        </TabsTrigger>
                        <TabsTrigger className="text-[11px]" value="issues">
                            Issues
                        </TabsTrigger>
                    </TabsList>
                </div>
                <TabsContent
                    value="generated-ui"
                    className="mt-0 min-h-0 overflow-hidden"
                >
                    <ScrollArea className="h-full">
                        <div className="px-3 pb-4">
                            <DetailRow
                                label="Template"
                                value={`${selectedModel.template} v${selectedModel.templateVersion}`}
                            />
                            <pre className="mt-2 font-mono text-[11px] break-words whitespace-pre-wrap">
                                {JSON.stringify(selectedModel, null, 4)}
                            </pre>
                        </div>
                    </ScrollArea>
                </TabsContent>
                <TabsContent
                    value="skeleton"
                    className="mt-0 min-h-0 overflow-hidden"
                >
                    <ScrollArea className="h-full">
                        <BindingSkeletonView
                            model={selectedModel}
                            selectedBindingId={selectedBindingId}
                            onSelectBinding={(bindingId) =>
                                selectBinding(
                                    selectedModel.widgetId,
                                    bindingId,
                                    'skeleton',
                                )
                            }
                        />
                    </ScrollArea>
                </TabsContent>
                <TabsContent
                    value="source"
                    className="mt-0 min-h-0 overflow-hidden"
                >
                    <ScrollArea className="h-full">
                        <SourceView
                            model={selectedModel}
                            binding={selectedBinding}
                        />
                    </ScrollArea>
                </TabsContent>
                <TabsContent
                    value="issues"
                    className="mt-0 min-h-0 overflow-hidden"
                >
                    <ScrollArea className="h-full">
                        <IssuesView
                            issues={selectedModel.issues}
                            onSelectBinding={(bindingId) =>
                                selectBinding(selectedModel.widgetId, bindingId)
                            }
                        />
                    </ScrollArea>
                </TabsContent>
            </Tabs>
        </div>
    );
}
